'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Label, Select, TextInput, ToggleSwitch } from 'flowbite-react';
import { toast } from 'react-toastify';
import { toastError } from '../../components/Toast.tsx';
import { BuildingView, useGetBuildingPicturesQuery, useUpdateBuildingMutation } from '../../lib/model/kantoordagApi.ts';

interface BuildingConfigFormProps {
    building: BuildingView;
}

function pictureFileName(pictureUrl: string): string {
    try {
        return new URL(pictureUrl).pathname.split('/').pop() ?? '';
    } catch {
        return '';
    }
}

// The caller renders this with key={building.name} (BuildingAdmin.tsx) so switching to a
// different building remounts it, re-initializing state from the new building's props -- no
// effect-based reset needed.
export default function BuildingConfigForm({ building }: BuildingConfigFormProps) {
    const { t } = useTranslation('building-admin');
    const { data: pictures, isLoading: picturesLoading } = useGetBuildingPicturesQuery();
    const [updateBuilding, updateBuildingResult] = useUpdateBuildingMutation();

    const [picture, setPicture] = useState(pictureFileName(building.picture));
    const [parkingSpots, setParkingSpots] = useState(String(building.parkingSpots));
    const [parkingGate, setParkingGate] = useState(building.parkingGate ?? '');
    const [hasParking, setHasParking] = useState(building.features.hasParking);
    const [hasRooms, setHasRooms] = useState(building.features.hasRooms);

    function save() {
        const spots = Number(parkingSpots);
        if (!Number.isInteger(spots) || spots < 0) {
            return;
        }
        updateBuilding({
            building: building.name,
            buildingUpdate: {
                parkingSpots: spots,
                parkingGate: parkingGate.trim() === '' ? null : parkingGate.trim(),
                picture,
                features: {
                    hasParking,
                    hasRooms,
                },
            },
        })
            .unwrap()
            .then(() => toast.success(t('building_saved')))
            .catch(() => toastError({ api: updateBuildingResult, message: t('building_save_failed') }));
    }

    return (
        <div data-testid="building-config-form" className="flex max-w-md flex-col gap-4">
            <h2 className="text-lg font-semibold">{t('config_title')}</h2>

            <div>
                <Label htmlFor="building-picture">{t('picture')}</Label>
                <Select
                    id="building-picture"
                    value={picture}
                    disabled={picturesLoading}
                    onChange={event => setPicture(event.target.value)}
                >
                    {pictures?.map(file => (
                        <option key={file} value={file}>{file}</option>
                    ))}
                </Select>
            </div>

            <div>
                <Label htmlFor="building-parking-spots">{t('parking_spots')}</Label>
                <TextInput
                    id="building-parking-spots"
                    type="number"
                    min={0}
                    value={parkingSpots}
                    onChange={event => setParkingSpots(event.target.value)}
                />
            </div>

            <div>
                <Label htmlFor="building-parking-gate">{t('parking_gate')}</Label>
                <TextInput
                    id="building-parking-gate"
                    value={parkingGate}
                    onChange={event => setParkingGate(event.target.value)}
                />
            </div>

            <ToggleSwitch
                checked={hasParking}
                onChange={setHasParking}
                label={t('has_parking')}
            />

            <div>
                <ToggleSwitch
                    checked={hasRooms}
                    onChange={setHasRooms}
                    label={t('has_rooms')}
                />
                {!hasRooms && (
                    <p className="mt-1 text-sm text-gray-500">{t('rooms_not_applicable')}</p>
                )}
            </div>

            <Button onClick={save} disabled={updateBuildingResult.isLoading}>
                {t('save')}
            </Button>
        </div>
    );
}
